import { z } from 'zod';
import { SCHOOL_TIME_ZONE } from './allotment';

export const REFERRAL_EXPORT_FIELDS = [
  'reference',
  'submitted_at',
  'status',
  'trustee_name',
  'trustee_email',
  'cycle',
  'family',
  'guardian_names',
  'guardian_emails',
  'child_name',
  'grade',
  'school',
  'stage',
  'relationship',
  'status_history',
] as const;
export const ReferralExportField = z.enum(REFERRAL_EXPORT_FIELDS);
export type ReferralExportField = z.infer<typeof ReferralExportField>;

export const REFERRAL_EXPORT_FIELD_LABELS: Record<ReferralExportField, string> = {
  reference: 'Reference',
  submitted_at: 'Submitted',
  status: 'Referral status',
  trustee_name: 'Trustee',
  trustee_email: 'Trustee email',
  cycle: 'Academic year',
  family: 'Family last name',
  guardian_names: 'Parent/guardian names',
  guardian_emails: 'Parent/guardian emails',
  child_name: 'Child',
  grade: 'Applying grade',
  school: 'Current school',
  stage: 'Applicant stage',
  relationship: 'Trustee relationship to family',
  status_history: 'Status history',
};

export const DEFAULT_REFERRAL_EXPORT_FIELDS: readonly ReferralExportField[] = [
  'reference',
  'submitted_at',
  'status',
  'trustee_name',
  'family',
  'child_name',
  'grade',
  'school',
  'stage',
];

export const REFERRAL_EXPORT_MAX_REFERRALS = 2000;
export const REFERRAL_EXPORT_TOO_LARGE = 'REFERRAL_EXPORT_TOO_LARGE';
export const REFERRAL_EXPORT_TITLE = 'Gulliver Prep · Trustee referrals';
export const REFERRAL_EXPORT_CONFIDENTIAL = 'Confidential: internal Admissions use only. Contains personal information about families and children; do not forward or store outside school systems.';

const FORMULA_START = /^[=+\-@\t\r]/;

export function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  const safe = FORMULA_START.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export const csvLine = (cells: readonly (string | number | null | undefined)[]) => cells.map(csvCell).join(',');

export interface ReferralExportCsvInput {
  generatedAt: string;
  generatedBy: string;
  filters: readonly { label: string; value: string }[];
  referralCount: number;
  headers: readonly string[];
  rows: readonly (readonly string[])[];
}

const generatedAtFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: SCHOOL_TIME_ZONE,
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

export const formatExportTimestamp = (iso: string) => generatedAtFormat.format(new Date(iso)).replace(/\u202f/g, ' ');

export const describeExportFilters = (filters: ReferralExportCsvInput['filters']) =>
  filters.length ? filters.map((f) => `${f.label}: ${f.value}`).join('; ') : 'None';

export function referralExportCsv(input: ReferralExportCsvInput): string {
  const lines = [
    csvLine([REFERRAL_EXPORT_TITLE]),
    csvLine(['CONFIDENTIAL', REFERRAL_EXPORT_CONFIDENTIAL]),
    csvLine(['Generated at', formatExportTimestamp(input.generatedAt)]),
    csvLine(['Generated by', input.generatedBy]),
    csvLine(['Filters', describeExportFilters(input.filters)]),
    csvLine(['Referrals', input.referralCount]),
    csvLine(['Rows', input.rows.length]),
    '',
    csvLine(input.headers),
    ...input.rows.map(csvLine),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
